import { PushProcessingNotification } from "@repo/helpers";
import { Button, ButtonTypes, Loading } from "@repo/ui";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import * as Queries from "../../../api/queries";
import { useTrainingApi } from "../../../api/trainingApi";
import { OnboardingCompletion, SingleOnboardingCompletion } from "../../../components/onboardingCompletion";
import { useRepContext } from "../../../contexts/editableItemContext";
import { useSegmentContext } from "../../../contexts/segmentContext";
import { useSettingsQuery, useUpdateRepData } from "../../../helpers/queryHooks";
import { Props } from "../types";
import { AssignTraining } from "./assignTraining";
import { MindFlashCustomFields } from "./mindFlashCustomFields";
import { MindFlashGrid } from "./mindFlashGrid";

export function MindFlash(props: Props) {
	const [settings] = useSettingsQuery();
	useUpdateRepData();

	if (!settings?.mindflashUserExists) return <NoUserFound {...props} />;

	return (
		<div>
			<MindFlashCompletion />
			<h1>TrakStar Training</h1>
			<AssignTraining {...props} />
			<MindFlashGrid />
			<MindFlashCustomFields {...props} />
		</div>
	);
}

function MindFlashCompletion() {
	const { segment } = useSegmentContext();

	if (segment)
		return <OnboardingCompletion req={"training"} key={"training"} depthToDisplay={0} repSegmentId={segment.id} />;

	return <SingleOnboardingCompletion req={"training"} key={"training"} depthToDisplay={0} />;
}

function NoUserFound(props: Props) {
	const { repId, rep } = useRepContext();
	const { createTrakStarUser } = useTrainingApi();
	const [saving, setSaving] = useState<string>(null);
	const queryClient = useQueryClient();
	const repHasEmail = !!rep.email;
	const createUser = useCallback(async () => {
		const notify = PushProcessingNotification("Creating User...");
		setSaving("Creating User...");
		try {
			const result = await createTrakStarUser(repId);
			if (result) {
				notify.success("Created!");
				setSaving("Loading TrakStar Data...");
				await queryClient.invalidateQueries({
					queryKey: [Queries.SETTINGS_REP],
				});
				await queryClient.invalidateQueries({ queryKey: [Queries.MIND_FLASH] });
				await queryClient.invalidateQueries({
					queryKey: [Queries.MIND_FLASH_CUSTOM_FIELDS],
				});
			} else {
				notify.error("Failed to create");
			}
		} catch (ex) {
			notify.error("Failed to create");
		} finally {
			setSaving(null);
		}
	}, [repId]);

	return (
		<Loading loading={!!saving} message={saving}>
			<h3>No TrakStar training data found.</h3>
			{repHasEmail && (
				<Button onClick={createUser} types={[ButtonTypes.Primary]} disabled={props.readOnly}>
					Create User
				</Button>
			)}
		</Loading>
	);
}
